import { createSlice } from '@reduxjs/toolkit';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import { AdminCommunityMemberModel } from '../models/CommunityMember.model';
import {
  adminUpdateUserVerificaiton,
  getAdminCommunityGuests,
  getAdminCommunityMembers,
} from './operators';
import updateCommunityMultiMember from './operators/UpdateMultiMember';
import { GuestModel } from '../../../admin/members/models/Guest.model';

export interface AdminCommunityMembersState {
  communityMembers: AdminCommunityMemberModel[];
  pagination: PaginationMetaModel;
  guests: GuestModel[];
  guestPagination: PaginationMetaModel;
  getAdminCommunityMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getAdminCommunityGuestsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  updateCommunityMemberMiltiStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  adminUpdateMemberVerificationStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: AdminCommunityMembersState = {
  communityMembers: [],
  pagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  guests: [],
  guestPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  getAdminCommunityMembersStatus: {
    status: 'idle',
    error: null,
  },
  getAdminCommunityGuestsStatus: {
    status: 'idle',
    error: null,
  },
  updateCommunityMemberMiltiStatus: {
    status: 'idle',
    error: null,
  },
  adminUpdateMemberVerificationStatus: {
    status: 'idle',
    error: null,
  },
};

const adminCommunityMembersSlice = createSlice({
  name: 'adminCommunityMembers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAdminCommunityMembers.pending, (state) => {
      state.getAdminCommunityMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(
      getAdminCommunityMembers.fulfilled,
      (state, { payload }) => {
        state.getAdminCommunityMembersStatus = {
          status: 'success',
          error: null,
        };
        state.communityMembers = payload.model;
        state.pagination = payload.meta;
      },
    );
    builder.addCase(getAdminCommunityMembers.rejected, (state, { payload }) => {
      state.getAdminCommunityMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch application administrator',
      };
    });
    builder.addCase(getAdminCommunityGuests.pending, (state) => {
      state.getAdminCommunityGuestsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getAdminCommunityGuests.fulfilled, (state, { payload }) => {
      state.getAdminCommunityGuestsStatus = {
        status: 'success',
        error: null,
      };
      state.guests = payload.model;
      state.guestPagination = payload.meta;
    });
    builder.addCase(getAdminCommunityGuests.rejected, (state, { payload }) => {
      state.getAdminCommunityGuestsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch application guests',
      };
    });
    builder.addCase(updateCommunityMultiMember.pending, (state) => {
      state.updateCommunityMemberMiltiStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(updateCommunityMultiMember.fulfilled, (state) => {
      state.updateCommunityMemberMiltiStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(
      updateCommunityMultiMember.rejected,
      (state, { payload }) => {
        state.updateCommunityMemberMiltiStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to update member multi status',
        };
      },
    );
    builder.addCase(adminUpdateUserVerificaiton.pending, (state) => {
      state.adminUpdateMemberVerificationStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(adminUpdateUserVerificaiton.fulfilled, (state) => {
      state.adminUpdateMemberVerificationStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(
      adminUpdateUserVerificaiton.rejected,
      (state, { payload }) => {
        state.adminUpdateMemberVerificationStatus = {
          status: 'failure',
          error: payload?.message
            ? payload.message
            : 'Error trying to verify user',
        };
      },
    );
    builder.addCase(resetAll, () => initialState);
  },
});

export default adminCommunityMembersSlice.reducer;
